import React, { useState, useEffect } from 'react';
import { App, Table, Tag, Input, Spin } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { MapContainer, TileLayer, Marker, Polygon, Popup } from 'react-leaflet';
import L from 'leaflet';
import apiClient from '../services/api';
import { useDebouncedSearch } from '../hooks/useDebouncedSearch';
import 'leaflet/dist/leaflet.css';

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

interface GeoLocation {
  id: string;
  name: string;
  type: string;
  customer_id: string;
  location_number: string;
}

interface GeoLocationGeometry extends GeoLocation {
  geometry: {
    type: string;
    coordinates: any;
  };
}

const GeoLocations: React.FC = () => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const [geoLocations, setGeoLocations] = useState<GeoLocation[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<GeoLocationGeometry | null>(null);
  const [mapLoading, setMapLoading] = useState(false);
  const { setSearch: setSearchText, debouncedSearch: debouncedSearchText } = useDebouncedSearch();

  useEffect(() => {
    loadGeoLocations();
  }, []);

  const loadGeoLocations = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/admin/geo-locations');
      setGeoLocations(response.data.geo_locations || []);
    } catch (error: any) {
      message.error(error.response?.data?.message || t('common.noData'));
    } finally {
      setLoading(false);
    }
  };

  const loadGeometry = async (id: string) => {
    try {
      setMapLoading(true);
      const response = await apiClient.get(`/admin/geo-locations/${id}/geometry`);
      setSelectedLocation(response.data.geo_location);
    } catch (error: any) {
      message.error(error.response?.data?.message || t('common.noData'));
    } finally {
      setMapLoading(false);
    }
  };

  const handleRowClick = (record: GeoLocation) => {
    loadGeometry(record.id);
  };

  const filteredLocations = geoLocations.filter((loc) =>
    loc.name.toLowerCase().includes(debouncedSearchText.toLowerCase())
  );

  const renderMapContent = () => {
    if (!selectedLocation?.geometry) return null;

    const { type, coordinates } = selectedLocation.geometry;

    if (type === 'Point') {
      return (
        <Marker position={[coordinates[1], coordinates[0]]}>
          <Popup>{selectedLocation.name}</Popup>
        </Marker>
      );
    }

    if (type === 'Polygon') {
      const positions = coordinates[0].map((coord: number[]) => [coord[1], coord[0]] as [number, number]);
      return (
        <Polygon positions={positions}>
          <Popup>{selectedLocation.name}</Popup>
        </Polygon>
      );
    }

    if (type === 'GeometryCollection') {
      return coordinates.map((geom: any, idx: number) => {
        if (geom.type === 'Point') {
          return (
            <Marker key={idx} position={[geom.coordinates[1], geom.coordinates[0]]}>
              <Popup>{selectedLocation.name}</Popup>
            </Marker>
          );
        }
        if (geom.type === 'Polygon') {
          const positions = geom.coordinates[0].map((coord: number[]) => [coord[1], coord[0]] as [number, number]);
          return (
            <Polygon key={idx} positions={positions}>
              <Popup>{selectedLocation.name}</Popup>
            </Polygon>
          );
        }
        return null;
      });
    }

    return null;
  };

  const typeColorMap: Record<string, string> = {
    Point: 'blue',
    Polygon: 'green',
    GeometryCollection: 'purple',
  };

  const typeLabelMap: Record<string, string> = {
    Point: t('geoLocations.typePoint'),
    Polygon: t('geoLocations.typePolygon'),
    GeometryCollection: t('geoLocations.typeGeometryCollection'),
  };

  const columns = [
    {
      title: t('common.name'),
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: t('geoLocations.type'),
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => (
        <Tag color={typeColorMap[type] || 'default'}>{typeLabelMap[type] || type}</Tag>
      ),
    },
    {
      title: t('geoLocations.customerId'),
      dataIndex: 'customer_id',
      key: 'customer_id',
    },
    {
      title: t('geoLocations.locationNumber'),
      dataIndex: 'location_number',
      key: 'location_number',
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1>{t('menu.geoLocations')}</h1>
      </div>

      <div style={{ display: 'flex', gap: 16 }}>
        <div style={{ flex: 1 }}>
          <Input
            placeholder={t('common.search')}
            prefix={<SearchOutlined />}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ marginBottom: 16 }}
            allowClear
          />
          <Table
            columns={columns}
            dataSource={filteredLocations}
            loading={loading}
            rowKey="id"
            onRow={(record) => ({
              onClick: () => handleRowClick(record),
              style: { cursor: 'pointer' },
            })}
            pagination={{ pageSize: 10 }}
          />
        </div>

        <div style={{ flex: 1, height: 500, position: 'relative' }}>
          {mapLoading && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(255,255,255,0.7)',
              zIndex: 1000,
            }}>
              <Spin />
            </div>
          )}
          <MapContainer
            center={[39.5, -8.0]}
            zoom={7}
            style={{ height: '100%', width: '100%', borderRadius: 8 }}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {renderMapContent()}
          </MapContainer>
        </div>
      </div>
    </div>
  );
};

export default GeoLocations;
